import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FaBook, FaSearch, FaFileExcel,
  FaTimes, FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import * as XLSX from 'xlsx-js-style';
import api from '../../../../utils/api';
import useColleges from '../../../../utils/useColleges';
import { useAuth } from '../../../../context/AuthContext';
import './Receipt.css';

/**
 * Cr Ledger:
 * - Cash mode → "Cash"
 * - Other mode + bank name saved → bank name
 * - Other mode + no bank name (old receipts) → payment mode (UPI, Cheque, etc.) — never "Cash"
 */
const getCrLedgerFromModes = (paymentModes) => {
  const modes = (paymentModes || []).filter((m) => Number(m.amount) > 0);
  if (!modes.length) return 'Cash';

  const labels = [];

  for (const m of modes) {
    const mode = (m.mode || 'Cash').trim();
    if (mode.toLowerCase() === 'cash') {
      if (!labels.includes('Cash')) labels.push('Cash');
      continue;
    }
    const bankName = (m.bankName || m.bank?.name || '').trim();
    const label = bankName || mode;
    if (label && !labels.includes(label)) labels.push(label);
  }

  return labels.length ? labels.join(' / ') : 'Cash';
};

const getDrLedger = (rec) => rec.drLedger || '—';
const getCrLedger = (rec) => rec.crLedger || getCrLedgerFromModes(rec.paymentModes);
const getNarration = (rec) => rec.narration || '';
const getGroup = (rec) => rec.group || 'DEBTORS';

/** Academic batch for Dr Ledger, e.g. 2021-22 */
const formatAcademicBatch = (raw) => {
  const s = String(raw || '').trim();
  if (!s || s === '—' || s === 'N/A') return '';

  const range = s.match(/^(\d{4})\s*[-–/]\s*(\d{2,4})$/);
  if (range) {
    const end = range[2].length === 4 ? range[2].slice(-2) : range[2];
    return `${range[1]}-${end.padStart(2, '0')}`;
  }

  if (/^\d{4}$/.test(s)) {
    const y = parseInt(s, 10);
    return `${y}-${String((y + 1) % 100).padStart(2, '0')}`;
  }

  return s;
};

/**
 * Admission batch — does NOT change when student is promoted or session changes.
 */
const getBatchLabel = (alloc, txn) => {
  const stored =
    txn?.studentBatch ||
    alloc?.batch ||
    alloc?.student?.admissionYear ||
    '';

  return formatAcademicBatch(stored) || '—';
};

const mapAllocationsToReceipts = (allocs, fromDate, toDate, collegeId) => {
  const rows = [];
  const collegeFilter = collegeId ? String(collegeId) : null;

  for (const alloc of allocs) {
    const allocCollegeId = String(alloc.college?._id || alloc.college || '');
    if (collegeFilter && allocCollegeId !== collegeFilter) continue;

    for (const txn of alloc.transactions || []) {
      const paid = Number(txn.paidAmount) || 0;
      if (paid <= 0) continue;
      if (!txn.receiptNo || String(txn.receiptNo).startsWith('PEND')) continue;

      const paidAt = txn.paidAt ? new Date(txn.paidAt) : null;
      if (!paidAt) continue;

      if (fromDate) {
        const f = new Date(fromDate);
        f.setHours(0, 0, 0, 0);
        if (paidAt < f) continue;
      }
      if (toDate) {
        const t = new Date(toDate);
        t.setHours(23, 59, 59, 999);
        if (paidAt > t) continue;
      }

      const studentName = (txn.studentName || alloc.student?.name || '—').trim();
      const fatherName = (alloc.student?.fatherName || '').trim();
      const batch = getBatchLabel(alloc, txn);
      const fatherPart =
        fatherName && fatherName !== '—' ? ` S/O ${fatherName}` : '';

      rows.push({
        _id: txn._id,
        date: paidAt,
        paidAt,
        receiptNo: txn.receiptNo,
        vchNo: txn.receiptNo,
        drLedger: `${studentName}${fatherPart} (${batch})`,
        paymentModes: txn.paymentModes || [],
        crLedger: getCrLedgerFromModes(txn.paymentModes),
        amountDr: paid,
        amountCr: paid,
        narration: txn.narration || '',
        group: 'DEBTORS',
        college: alloc.college,
      });
    }
  }

  rows.sort((a, b) => new Date(a.date) - new Date(b.date));
  return rows;
};

const Receipt = () => {
  const { user } = useAuth();
  const { colleges: collegesList, loading: collegesLoading } = useColleges();
  const isScoped = user?.role === 'collegeadmin' || user?.role === 'clerk';

  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCollege, setFilterCollege] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    if (isScoped && collegesList.length > 0 && !filterCollege) {
      setFilterCollege(collegesList[0]._id);
    }
  }, [isScoped, collegesList, filterCollege]);

  const fetchReceipts = useCallback(async () => {
    if (!filterCollege) {
      setReceipts([]);
      setFetchError('');
      return;
    }

    setLoading(true);
    setFetchError('');
    setReceipts([]);

    try {
      const { data } = await api.get('/fees/allocations', {
        params: { college: filterCollege, _t: Date.now() },
      });
      const allocs = data?.data || [];
      setReceipts(mapAllocationsToReceipts(allocs, fromDate, toDate, filterCollege));
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load receipt data';
      setFetchError(msg);
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  }, [filterCollege, fromDate, toDate]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const handleClearFilters = () => {
    setSearchQuery('');
    if (!isScoped) setFilterCollege('');
    setFromDate('');
    setToDate('');
    if (!isScoped) setReceipts([]);
  };

  const filteredReceipts = useMemo(() => {
    const search = searchQuery.toLowerCase().trim();
    return receipts.filter((rec) => {
      if (!search) return true;
      const vchNo = (rec.receiptNo || rec.vchNo || '').toLowerCase();
      const drLedger = getDrLedger(rec).toLowerCase();
      const crLedger = getCrLedger(rec).toLowerCase();
      const narration = getNarration(rec).toLowerCase();
      return (
        vchNo.includes(search) ||
        drLedger.includes(search) ||
        crLedger.includes(search) ||
        narration.includes(search)
      );
    });
  }, [receipts, searchQuery]);

  const selectedCollege = useMemo(
    () => collegesList.find((c) => String(c._id) === String(filterCollege)),
    [collegesList, filterCollege]
  );

  const totalAmountDr = useMemo(
    () => filteredReceipts.reduce((sum, rec) => sum + (Number(rec.amountDr) || 0), 0),
    [filteredReceipts]
  );

  const totalAmountCr = useMemo(
    () => filteredReceipts.reduce((sum, rec) => sum + (Number(rec.amountCr) || 0), 0),
    [filteredReceipts]
  );

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleExportExcel = () => {
    const headers = [
      'Date', 'VCh No', 'Dr Ledger', 'Cr Ledger', 'Amount Dr', 'Amount Cr', 'Narations', 'GROUP',
    ];

    const rows = filteredReceipts.map((rec) => [
      rec.date || rec.paidAt
        ? new Date(rec.date || rec.paidAt).toLocaleDateString('en-GB')
        : '—',
      rec.receiptNo || rec.vchNo || '—',
      getDrLedger(rec),
      getCrLedger(rec),
      Number(rec.amountDr) || 0,
      Number(rec.amountCr) || 0,
      getNarration(rec),
      getGroup(rec),
    ]);

    const aoa = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws['!cols'] = [
      { wch: 12 },
      { wch: 18 },
      { wch: 40 },
      { wch: 22 },
      { wch: 14 },
      { wch: 14 },
      { wch: 45 },
      { wch: 15 },
    ];

    const filenameParts = ['Receipt_Register'];
    if (selectedCollege) {
      const cleanName = selectedCollege.name.replace(/[^a-zA-Z0-9]/g, '_').replace(/__+/g, '_');
      filenameParts.push(cleanName);
    }
    if (fromDate && toDate) filenameParts.push(`${fromDate}_to_${toDate}`);
    else if (fromDate) filenameParts.push(`from_${fromDate}`);
    else if (toDate) filenameParts.push(`to_${toDate}`);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Receipt Register');
    XLSX.writeFile(wb, `${filenameParts.join('_')}.xlsx`);
  };

  const showSelectCollegeMsg = !filterCollege && !collegesLoading;

  return (
    <div className="tele-export-receipt-container">
      <div className="tele-export-receipt-header-row">
        <h2 className="tele-export-receipt-title">
          <FaBook /> Receipt Register Ledger
        </h2>
        <div className="tele-export-receipt-export-btns">
          <button
            type="button"
            className="tele-export-receipt-btn-excel"
            onClick={handleExportExcel}
            disabled={filteredReceipts.length === 0}
          >
            <FaFileExcel /> Export to Excel
          </button>
        </div>
      </div>

      {filterCollege && filteredReceipts.length > 0 && (
        <div className="tele-export-receipt-summary-strip">
          <span>
            Showing <strong>{filteredReceipts.length}</strong> paid fee receipts
            {selectedCollege ? <> for <strong>{selectedCollege.name}</strong></> : null}
          </span>
          <div className="tele-export-receipt-summary-amounts">
            <span className="tele-export-receipt-summary-amount">
              Total Dr: <strong>₹ {totalAmountDr.toLocaleString()}</strong>
            </span>
            <span className="tele-export-receipt-summary-amount" style={{ marginLeft: '15px' }}>
              Total Cr: <strong>₹ {totalAmountCr.toLocaleString()}</strong>
            </span>
          </div>
        </div>
      )}

      <div className="tele-export-receipt-controls">
        <div className="tele-export-receipt-search-wrapper">
          <FaSearch className="tele-export-receipt-search-icon" />
          <input
            type="text"
            className="tele-export-receipt-search-input"
            placeholder="Search student, voucher, bank, narration..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={!filterCollege}
          />
        </div>

        {!isScoped && (
          <select
            className="tele-export-receipt-select"
            value={filterCollege}
            onChange={(e) => setFilterCollege(e.target.value)}
            disabled={collegesLoading}
          >
            <option value="">Select College</option>
            {collegesList.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        {isScoped && selectedCollege && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 14px',
              background: '#f1f5f9',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#334155',
            }}
            title="Your college"
          >
            🏫 {selectedCollege.name}
          </span>
        )}

        <input
          type="date"
          className="tele-export-receipt-select"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          title="From Date"
          disabled={!filterCollege}
        />

        <input
          type="date"
          className="tele-export-receipt-select"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          min={fromDate}
          title="To Date"
          disabled={!filterCollege}
        />

        {(filterCollege && !isScoped) || fromDate || toDate || searchQuery ? (
          <button type="button" className="tele-export-receipt-btn-clear" onClick={handleClearFilters}>
            <FaTimes /> Clear
          </button>
        ) : null}
      </div>

      <div className="tele-export-receipt-grouped-container">
        {collegesLoading ? (
          <div className="tele-export-receipt-state-msg">Loading colleges...</div>
        ) : showSelectCollegeMsg ? (
          <div className="tele-export-receipt-state-msg tele-export-receipt-empty">
            Please select a college to view paid fee receipts for Tally export.
          </div>
        ) : loading ? (
          <div className="tele-export-receipt-state-msg">Loading receipt records...</div>
        ) : fetchError ? (
          <div className="tele-export-receipt-state-msg tele-export-receipt-empty">{fetchError}</div>
        ) : filteredReceipts.length === 0 ? (
          <div className="tele-export-receipt-state-msg tele-export-receipt-empty">
            No paid fee receipts found for the selected college and date range.
          </div>
        ) : (
          <div className="tele-export-receipt-table-group">
            <h3 className="tele-export-receipt-group-header">
              <span className="tele-export-receipt-college-name">
                🏫 {selectedCollege?.name || 'College'}
              </span>
              <div className="tele-export-receipt-group-stats">
                <span className="tele-export-receipt-group-count">
                  {filteredReceipts.length} entries
                </span>
                <span className="tele-export-receipt-group-total">
                  Dr: ₹ {totalAmountDr.toLocaleString()} | Cr: ₹ {totalAmountCr.toLocaleString()}
                </span>
              </div>
            </h3>
            <div className="tele-export-receipt-table-wrapper">
              <table className="tele-export-receipt-table">
                <thead>
                  <tr>
                    <th width="55px">S.No</th>
                    <th className="tele-export-receipt-col-date">Date</th>
                    <th className="tele-export-receipt-col-vch">VCH No</th>
                    <th className="tele-export-receipt-col-dr tele-export-receipt-align-left">Dr Ledger</th>
                    <th className="tele-export-receipt-col-cr tele-export-receipt-align-left">Cr Ledger</th>
                    <th className="tele-export-receipt-col-amount-dr">Amount Dr</th>
                    <th className="tele-export-receipt-col-amount-cr">Amount Cr</th>
                    <th className="tele-export-receipt-col-narration tele-export-receipt-align-left">Narrations</th>
                    <th className="tele-export-receipt-col-group">Group</th>
                    <th className="tele-export-receipt-col-expand-header" width="70px">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.map((rec, index) => (
                    <React.Fragment key={rec._id || `${rec.receiptNo}-${index}`}>
                      <tr className="tele-export-receipt-main-row">
                        <td>{index + 1}</td>
                        <td className="tele-export-receipt-col-date" style={{ fontWeight: 600, color: '#334155' }}>
                          {fmtDate(rec.date || rec.paidAt)}
                        </td>
                        <td className="tele-export-receipt-col-vch" style={{ fontWeight: 600, color: '#475569' }}>
                          {rec.receiptNo || rec.vchNo || '—'}
                        </td>
                        <td className="tele-export-receipt-col-dr tele-export-receipt-align-left" style={{ fontWeight: 700, color: '#0f172a' }}>
                          {getDrLedger(rec)}
                        </td>
                        <td className="tele-export-receipt-col-cr tele-export-receipt-align-left" style={{ fontWeight: 500 }}>
                          {getCrLedger(rec)}
                        </td>
                        <td className="tele-export-receipt-col-amount-dr tele-export-receipt-amt-total">
                          ₹ {Number(rec.amountDr || 0).toLocaleString()}
                        </td>
                        <td className="tele-export-receipt-col-amount-cr tele-export-receipt-amt-total" style={{ color: '#16a34a' }}>
                          ₹ {Number(rec.amountCr || 0).toLocaleString()}
                        </td>
                        <td className="tele-export-receipt-col-narration tele-export-receipt-align-left">
                          <div className="tele-export-receipt-truncate-text">
                            {getNarration(rec) || '—'}
                          </div>
                        </td>
                        <td className="tele-export-receipt-col-group" style={{ fontWeight: 600, color: '#475569' }}>
                          {getGroup(rec)}
                        </td>
                        <td className="tele-export-receipt-col-expand-trigger">
                          <button
                            type="button"
                            className="tele-export-receipt-expand-btn"
                            onClick={() => setExpandedRow(expandedRow === rec._id ? null : rec._id)}
                            aria-label="Toggle details"
                          >
                            {expandedRow === rec._id ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </td>
                      </tr>

                      {expandedRow === rec._id && (
                        <tr className="tele-export-receipt-expanded-row">
                          <td colSpan="10" style={{ padding: 0 }}>
                            <div className="tele-export-receipt-expanded-content">
                              <div className="tele-export-receipt-expanded-grid">
                                <div className="tele-export-receipt-exp-item tele-export-receipt-exp-date">
                                  <span className="tele-export-receipt-exp-label">Date</span>
                                  <span className="tele-export-receipt-exp-value">{fmtDate(rec.date || rec.paidAt)}</span>
                                </div>
                                <div className="tele-export-receipt-exp-item tele-export-receipt-exp-vch">
                                  <span className="tele-export-receipt-exp-label">VCH No</span>
                                  <span className="tele-export-receipt-exp-value">{rec.receiptNo || rec.vchNo || '—'}</span>
                                </div>
                                <div className="tele-export-receipt-exp-item tele-export-receipt-exp-dr">
                                  <span className="tele-export-receipt-exp-label">Dr Ledger</span>
                                  <span className="tele-export-receipt-exp-value">{getDrLedger(rec)}</span>
                                </div>
                                <div className="tele-export-receipt-exp-item tele-export-receipt-exp-cr">
                                  <span className="tele-export-receipt-exp-label">Cr Ledger</span>
                                  <span className="tele-export-receipt-exp-value">{getCrLedger(rec)}</span>
                                </div>
                                <div className="tele-export-receipt-exp-item tele-export-receipt-exp-amount-dr">
                                  <span className="tele-export-receipt-exp-label">Amount Dr</span>
                                  <span className="tele-export-receipt-exp-value" style={{ color: '#dc2626', fontWeight: 700 }}>
                                    ₹ {Number(rec.amountDr || 0).toLocaleString()}
                                  </span>
                                </div>
                                <div className="tele-export-receipt-exp-item tele-export-receipt-exp-amount-cr">
                                  <span className="tele-export-receipt-exp-label">Amount Cr</span>
                                  <span className="tele-export-receipt-exp-value" style={{ color: '#16a34a', fontWeight: 700 }}>
                                    ₹ {Number(rec.amountCr || 0).toLocaleString()}
                                  </span>
                                </div>
                                <div className="tele-export-receipt-exp-item tele-export-receipt-exp-narration">
                                  <span className="tele-export-receipt-exp-label">Narration</span>
                                  <span className="tele-export-receipt-exp-value">{getNarration(rec) || '—'}</span>
                                </div>
                                <div className="tele-export-receipt-exp-item tele-export-receipt-exp-group">
                                  <span className="tele-export-receipt-exp-label">Group</span>
                                  <span className="tele-export-receipt-exp-value">{getGroup(rec)}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Receipt;